import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
    Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext,
} from "@/components/ui/carousel";
import { LogoMark } from "@/components/LogoMark";
import {
    ShieldCheck, Crosshair, Users, Gem, Check, X, TrendingUp, Tag, Lock, Globe,
    Star, ArrowRight, Cpu, Eye, ShoppingBag, Store, Quote, BadgeCheck,
} from "lucide-react";

const img = (id, w = 400) => `https://images.unsplash.com/photo-${id}?w=${w}&q=80&auto=format&fit=crop`;

// ---------- small in-view + count-up helpers (interactivity) ----------
function useInView(threshold = 0.3) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
            { threshold }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

function CountUp({ end, prefix = "", suffix = "", decimals = 0, duration = 1300 }) {
    const [ref, inView] = useInView(0.4);
    const [val, setVal] = useState(0);
    useEffect(() => {
        if (!inView) return;
        let raf;
        const start = performance.now();
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(end * eased);
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [inView, end, duration]);
    return <span ref={ref}>{prefix}{val.toFixed(decimals)}{suffix}</span>;
}

const HERO_BADGES = [
    { title: "Fair for Buyers", text: "Everyone has an equal shot." },
    { title: "Built on Trust", text: "Transparency in every auction." },
    { title: "Fair for Sellers", text: "Real value. Better results." },
    { title: "Better for Everyone", text: "A marketplace that works for all." },
];

const HERO_PILLS = [
    { icon: ShieldCheck, title: "No Hidden Reserves", text: "What you see is what you get." },
    { icon: Crosshair, title: "No Sniping", text: "All bids in at the same time." },
    { icon: Users, title: "No Bidding Wars", text: "One bid. Your best and final." },
    { icon: Gem, title: "True Market Value", text: "The highest value wins — always." },
];

const TRADITIONAL = [
    "Hidden reserve prices",
    "Bidding wars drive up prices",
    "Last-second sniping",
    "Robo-bidding & bid manipulation",
    "Unfair advantages for some",
    "Unclear true market value",
];
const OMNIAUC = [
    "No hidden reserves",
    "No bidding wars",
    "No sniping — all bids in together",
    "No bots. No manipulation",
    "A level playing field for everyone",
    "True market value revealed",
];

const WHY_MATTERS = [
    { icon: ShieldCheck, title: "Fairness First", text: "No tricks. No loopholes. Just a level playing field for everyone." },
    { icon: Users, title: "More Trust", text: "Transparency builds trust. Trust builds a better community." },
    { icon: Tag, title: "Better Value", text: "Real competition. Real value. Real results." },
    { icon: Lock, title: "Safer Experience", text: "Verified users, secure payments, and protection built in." },
    { icon: Globe, title: "Built for Everyone", text: "From everyday buyers to global businesses — OmniAuc works for you." },
];

// Honest platform facts (not unverified user counts) for a Beta-stage product.
const STATS = [
    { value: 100, suffix: "%", label: "Sealed & Private", sub: "Bids hidden until close" },
    { value: 1, suffix: "", label: "Bid Per Item", sub: "Your best, final shot" },
    { value: 0, suffix: "", label: "Snipers & Bots", sub: "Impossible by design" },
    { value: 256, suffix: "-bit", label: "Secure Payments", sub: "Encrypted via Stripe" },
];

const TESTIMONIALS = [
    { quote: "OmniAuc is the most fair auction platform I've used. Finally, a level playing field for everyone.", name: "Sarah T.", role: "Collector", initials: "ST" },
    { quote: "As a seller, I get better results and serious buyers without the games.", name: "Mark D.", role: "Business Owner", initials: "MD" },
    { quote: "No sniping. No hidden reserves. Just great auctions and real value.", name: "Jason R.", role: "Auction Enthusiast", initials: "JR" },
    { quote: "One private bid and I won at a fair price. No stress, no bidding wars.", name: "Priya N.", role: "First-time Buyer", initials: "PN" },
];

const CHART_BARS = [22, 30, 36, 48, 55, 68, 82, 100];

const CTA_IMAGES = [
    { id: "1516035069371-29a1b244cc32", alt: "Camera" },
    { id: "1496181133206-80ce9b88a853", alt: "Laptop" },
    { id: "1523275335684-37898b6baf30", alt: "Watch" },
];

const TRUST_STRIP = [
    { icon: ShoppingBag, label: "Fair for Buyers" },
    { icon: Store, label: "Fair for Sellers" },
    { icon: ShieldCheck, label: "Built on Trust" },
    { icon: Cpu, label: "Powered by Technology" },
    { icon: Eye, label: "Driven by Transparency" },
];

function GrowthChart() {
    const [ref, inView] = useInView(0.4);
    return (
        <Card className="p-6 sm:p-8" data-testid="why-growth-chart">
            <h3 className="font-display text-xl font-bold leading-snug">Better Results.<br />For Buyers and Sellers.</h3>
            <div ref={ref} className="mt-6 flex h-40 items-end gap-2">
                {CHART_BARS.map((h, i) => (
                    <div
                        key={i}
                        className="flex-1 rounded-t bg-gradient-to-t from-brass/30 to-brass transition-[height] duration-700 ease-out"
                        style={{ height: inView ? `${h}%` : "0%", transitionDelay: `${i * 70}ms` }}
                    />
                ))}
            </div>
            <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
                Sellers get stronger results. Buyers win with confidence. <span className="font-medium text-foreground">The market wins.</span>
            </p>
        </Card>
    );
}

export default function Compare() {
    return (
        <div data-testid="why-omniauc-page">
            {/* ---------------- HERO ---------------- */}
            <section className="noise-overlay relative overflow-hidden border-b border-border">
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_55%_at_15%_0%,rgba(33,140,255,0.16)_0%,rgba(33,140,255,0)_55%)]" />
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(55%_55%_at_85%_25%,rgba(214,158,46,0.16)_0%,rgba(214,158,46,0)_60%)]" />
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-20">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brass">Why OmniAuc</span>
                        <h1 className="mt-3 font-display text-4xl font-extrabold leading-[1.04] tracking-tight sm:text-5xl">
                            The Fair Auction Marketplace.
                            <span className="block bg-gradient-to-r from-brass to-[hsl(38_92%_62%)] bg-clip-text text-transparent">Reimagined.</span>
                        </h1>
                        <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
                            OmniAuc was built to fix what's broken in online auctions. We remove the unfair advantages, hidden agendas,
                            and outdated tactics — so the highest value wins, every single time.
                        </p>
                        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
                            {HERO_PILLS.map(({ icon: Icon, title, text }) => (
                                <div key={title}>
                                    <Icon className="h-6 w-6 text-brass" />
                                    <h3 className="mt-2 text-sm font-bold">{title}</h3>
                                    <p className="mt-0.5 text-xs text-muted-foreground">{text}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Glowing emblem + trust badges */}
                    <div className="relative">
                        <div className="relative mx-auto flex h-44 w-44 items-center justify-center">
                            <div className="absolute inset-0 rounded-full bg-brass/20 blur-3xl" />
                            <div className="absolute inset-2 rounded-full border border-brass/30" />
                            <div className="absolute inset-8 rounded-full border border-electric/20" />
                            <LogoMark size={84} />
                        </div>
                        <div className="mt-8 grid grid-cols-2 gap-3">
                            {HERO_BADGES.map(({ title, text }) => (
                                <div key={title} className="rounded-xl border border-border bg-card p-4" data-testid="why-hero-badge">
                                    <div className="flex items-center gap-2">
                                        <span className="flex h-5 w-5 items-center justify-center rounded-full border border-brass/40 text-brass"><Check className="h-3 w-3" /></span>
                                        <h3 className="text-sm font-bold">{title}</h3>
                                    </div>
                                    <p className="mt-1.5 text-xs text-muted-foreground">{text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------------- VS COMPARISON ---------------- */}
            <section className="bg-background">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
                    <div className="text-center">
                        <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                            <span className="text-brass">OmniAuc</span> vs. Traditional Auctions
                        </h2>
                        <p className="mt-2 text-muted-foreground">See the difference fairness makes.</p>
                    </div>

                    <div className="mt-12 grid gap-6 lg:grid-cols-3">
                        {/* Comparison table spans 2 cols */}
                        <div className="lg:col-span-2 grid gap-4 sm:grid-cols-2" data-testid="why-vs-table">
                            <Card className="overflow-hidden p-0">
                                <div className="border-b border-border bg-secondary/40 px-5 py-4">
                                    <h3 className="font-display text-sm font-bold text-muted-foreground">The Problem with Traditional Auctions</h3>
                                </div>
                                <ul className="divide-y divide-border">
                                    {TRADITIONAL.map((t) => (
                                        <li key={t} className="flex items-start gap-3 px-5 py-3.5 text-sm">
                                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-destructive/15 text-destructive"><X className="h-3 w-3" /></span>
                                            <span className="text-muted-foreground">{t}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                            <Card className="overflow-hidden border-brass/30 p-0">
                                <div className="border-b border-brass/30 bg-brass/15 px-5 py-4">
                                    <h3 className="font-display text-sm font-bold text-brass">The OmniAuc Difference</h3>
                                </div>
                                <ul className="divide-y divide-border">
                                    {OMNIAUC.map((t) => (
                                        <li key={t} className="flex items-start gap-3 px-5 py-3.5 text-sm">
                                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success/15 text-success"><Check className="h-3 w-3" /></span>
                                            <span className="font-medium">{t}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        </div>

                        {/* Growth chart */}
                        <GrowthChart />
                    </div>
                </div>
            </section>

            {/* ---------------- WHY IT MATTERS ---------------- */}
            <section className="border-y border-border bg-[hsl(221_59%_7%)]">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
                    <h2 className="text-center font-display text-3xl font-bold tracking-tight sm:text-4xl">
                        Why It <span className="text-brass">Matters</span>
                    </h2>
                    <div className="mx-auto mt-3 h-1 w-16 rounded-full bg-brass" />
                    <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
                        {WHY_MATTERS.map(({ icon: Icon, title, text }) => (
                            <div key={title} className="rounded-2xl border border-border bg-card p-6 text-center transition-colors hover:border-brass/40" data-testid="why-matters-card">
                                <div className="relative mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brass/20 to-electric/10 ring-1 ring-brass/30">
                                    <Icon className="h-6 w-6 text-brass" />
                                    <div className="absolute inset-0 -z-10 rounded-2xl bg-brass/20 blur-xl" />
                                </div>
                                <h3 className="mt-4 font-display text-sm font-bold uppercase tracking-wide">{title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------------- TRUSTED COMMUNITY ---------------- */}
            <section className="bg-background">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
                    <div className="grid items-start gap-12 lg:grid-cols-2">
                        <div>
                            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                                Built on a <span className="text-brass">Foundation of Fairness</span>
                            </h2>
                            <p className="mt-4 max-w-md text-muted-foreground">
                                Every rule on OmniAuc exists to protect both sides of the deal — transparency, privacy, and a result you can trust.
                            </p>
                            <div className="mt-8 grid grid-cols-2 gap-5">
                                {STATS.map((s) => (
                                    <div key={s.label} className="rounded-xl border border-border bg-card p-5" data-testid="why-stat">
                                        <p className="font-display text-3xl font-extrabold text-brass">
                                            <CountUp end={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
                                        </p>
                                        <p className="mt-1 text-sm font-semibold">{s.label}</p>
                                        <p className="text-xs text-muted-foreground">{s.sub}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Testimonials carousel */}
                        <div>
                            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Quote className="h-4 w-4 text-brass" /> What early members are saying
                                </div>
                                <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/40 px-2.5 py-1 text-[11px] font-medium text-muted-foreground" data-testid="testimonials-sample-badge">
                                    <Star className="h-3 w-3 text-brass" /> Sample placeholders
                                </span>
                            </div>
                            <Carousel opts={{ align: "start", loop: true }} className="w-full" data-testid="why-testimonials">
                                <CarouselContent>
                                    {TESTIMONIALS.map((t) => (
                                        <CarouselItem key={t.name} className="sm:basis-4/5">
                                            <Card className="h-full p-6">
                                                <div className="flex gap-0.5 text-brass">
                                                    {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                                                </div>
                                                <p className="mt-3 text-sm leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                                                <div className="mt-5 flex items-center gap-3">
                                                    <Avatar className="h-9 w-9">
                                                        <AvatarFallback className="bg-brass/15 text-xs font-bold text-brass">{t.initials}</AvatarFallback>
                                                    </Avatar>
                                                    <div>
                                                        <p className="text-sm font-semibold">{t.name}</p>
                                                        <p className="text-xs text-muted-foreground">{t.role}</p>
                                                    </div>
                                                </div>
                                            </Card>
                                        </CarouselItem>
                                    ))}
                                </CarouselContent>
                                <div className="mt-4 flex items-center justify-end gap-2">
                                    <CarouselPrevious className="static translate-y-0" data-testid="testimonial-prev" />
                                    <CarouselNext className="static translate-y-0" data-testid="testimonial-next" />
                                </div>
                            </Carousel>
                            <p className="mt-3 text-xs italic text-muted-foreground" data-testid="testimonials-disclaimer">
                                Sample reviews shown for illustration. Real member reviews will appear here as our community grows.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------------- CTA BANNER ---------------- */}
            <section className="relative overflow-hidden border-t border-border bg-[hsl(221_59%_7%)]">
                <div className="absolute inset-y-0 right-0 -z-10 w-2/3 bg-[radial-gradient(60%_120%_at_100%_50%,rgba(214,158,46,0.18)_0%,rgba(214,158,46,0)_60%)]" />
                <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 px-4 py-14 sm:px-6 lg:flex-row lg:justify-between">
                    <div className="text-center lg:text-left">
                        <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                            Experience the <span className="text-brass">OmniAuc</span> Difference
                        </h2>
                        <p className="mt-2 max-w-lg text-muted-foreground">
                            Fair auctions. True value. Better results. Be part of the future of online auctions.
                        </p>
                        <div className="mt-6 flex flex-wrap justify-center gap-3 lg:justify-start">
                            <Button asChild size="lg" className="rounded-[10px]" data-testid="why-start-bidding">
                                <Link to="/auctions">Start bidding <ArrowRight className="ml-2 h-4 w-4" /></Link>
                            </Button>
                            <Button asChild size="lg" variant="outline" className="rounded-[10px]" data-testid="why-start-selling">
                                <Link to="/signup"><Store className="mr-2 h-4 w-4" /> Start selling</Link>
                            </Button>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        {CTA_IMAGES.map(({ id, alt }) => (
                            <div key={id} className="h-24 w-24 overflow-hidden rounded-xl border border-border sm:h-28 sm:w-28">
                                <img src={img(id, 300)} alt={alt} loading="lazy" className="h-full w-full object-cover" />
                            </div>
                        ))}
                    </div>
                </div>
                {/* Trust strip */}
                <div className="border-t border-border">
                    <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-3 px-4 py-6 sm:px-6">
                        {TRUST_STRIP.map(({ icon: Icon, label }) => (
                            <span key={label} className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                                <Icon className="h-4 w-4 text-electric" /> {label}
                            </span>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
